<template>
  <form class="flex flex-col gap-2" @submit.prevent="addChecklistItem">
    <div class="flex items-center gap-2">
      <Input :id="inputId" v-model="instruction" class="flex-1" placeholder="Instruction" :disabled="isAddingChecklistItem" />

      <Button type="submit" size="sm" :loading="isAddingChecklistItem" :disabled="!canAddChecklistItem && !isAddingChecklistItem"> Add Item </Button>
    </div>

    <p v-if="errorMessage" class="text-xs text-destructive">
      {{ errorMessage }}
    </p>
  </form>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { Button } from '@/components/ui/button/index.js'
import { Input } from '@/components/ui/input/index.js'
import { useChecklistItemsStore } from '../../stores/checklistItemsStore.js'

const props = defineProps({
  checklistId: {
    type: [Number, String],
    required: true,
  },
  focusSignal: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['added'])

const checklistItemsStore = useChecklistItemsStore(`checklist_${props.checklistId}`)
const instruction = ref('')
const errorMessage = ref(null)
const inputId = computed(() => `checklist-item-add-${props.checklistId}`)
const isAddingChecklistItem = computed(() => {
  return checklistItemsStore.isAddingChecklistItem(props.checklistId)
})
const canAddChecklistItem = computed(() => {
  return instruction.value.trim().length > 0
})

const focusInput = async () => {
  await nextTick()

  const nativeInput = document.getElementById(inputId.value)
  if (!nativeInput) {
    return
  }

  nativeInput.focus()
}

const addChecklistItem = async () => {
  if (!canAddChecklistItem.value) {
    return
  }

  errorMessage.value = null

  try {
    const checklistItem = await checklistItemsStore.createItemForChecklist({
      checklistId: props.checklistId,
      instruction: instruction.value,
    })

    instruction.value = ''
    emit('added', checklistItem)
    await focusInput()
  } catch (error) {
    errorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to add checklist item.'
  }
}

onMounted(async () => {
  await focusInput()
})

watch(
  () => props.focusSignal,
  async (signal) => {
    if (Number(signal) < 1) {
      return
    }

    await focusInput()
  },
)

defineExpose({
  focusInput,
})
</script>
